/**
 * ====================================================================
 * FIKO CAD DIMENSION TOOLTIP
 * ====================================================================
 *
 * Hover durumunda bilgi tooltip'i
 * Dimension tipi, değer, tolerans bilgileri
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import { TooltipConfig, DimensionVisualSettings } from './DimensionVisualStyle';
import { Dimension, DimensionType } from '../DimensionTypes';

// ====================================================================
// TOOLTIP CLASS
// ====================================================================

/**
 * Dimension Tooltip - Hover bilgi paneli
 */
export class DimensionTooltip {
    // DOM elementi
    private container: HTMLDivElement;

    // Durum
    private isVisible: boolean = false;
    private currentDimension: Dimension | null = null;
    private hideTimeout: number | null = null;
    private showTimeout: number | null = null;

    // Konfigürasyon
    private config: TooltipConfig;

    // Singleton instance
    private static instance: DimensionTooltip;

    private constructor() {
        this.config = this.getDefaultConfig();
        this.container = this.createContainer();
        document.body.appendChild(this.container);

        // Resize listener
        window.addEventListener('resize', this.handleResize.bind(this));
    }

    /**
     * Singleton instance al
     */
    static getInstance(): DimensionTooltip {
        if (!this.instance) {
            this.instance = new DimensionTooltip();
        }
        return this.instance;
    }

    /**
     * Varsayılan konfigürasyon
     */
    private getDefaultConfig(): TooltipConfig {
        return {
            enabled: true,
            showDelay: 400,
            hideDelay: 80,
            backgroundColor: 'rgba(44, 62, 80, 0.95)',
            textColor: '#ECF0F1',
            fontSize: 12,
            borderRadius: 6,
            padding: { x: 10, y: 8 },
            showDimensionType: true,
            showTolerance: true,
            showId: true,
            maxWidth: 280,
            position: 'cursor',
            offset: { x: 15, y: 15 }
        };
    }

    /**
     * Konfigürasyonu güncelle
     */
    setConfig(config: Partial<TooltipConfig>): void {
        this.config = { ...this.config, ...config };
        this.updateContainerStyle();
    }

    /**
     * Container oluştur
     */
    private createContainer(): HTMLDivElement {
        const div = document.createElement('div');
        div.id = 'fiko-dimension-tooltip';
        div.style.cssText = this.getContainerStyle();
        return div;
    }

    /**
     * Container stili
     */
    private getContainerStyle(): string {
        return `
            position: fixed;
            background: ${this.config.backgroundColor};
            color: ${this.config.textColor};
            padding: ${this.config.padding.y}px ${this.config.padding.x}px;
            border-radius: ${this.config.borderRadius}px;
            font-family: 'Segoe UI', Roboto, Arial, sans-serif;
            font-size: ${this.config.fontSize}px;
            pointer-events: none;
            opacity: 0;
            transform: translateY(5px);
            transition: opacity 0.15s ease, transform 0.15s ease;
            z-index: 100000;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
            max-width: ${this.config.maxWidth}px;
            white-space: nowrap;
            line-height: 1.4;
        `;
    }

    /**
     * Container stilini güncelle
     */
    private updateContainerStyle(): void {
        this.container.style.cssText = this.getContainerStyle();
    }

    /**
     * Tooltip'i göster
     * Overload 1: Dimension + screenPosition object
     * Overload 2: text string + x, y coordinates
     */
    show(
        dimensionOrText: Dimension | string,
        screenPositionOrX?: { x: number; y: number } | number,
        y?: number
    ): void {
        if (!this.config.enabled) return;

        // Timeout'ları temizle
        this.clearTimeouts();

        // Parametreleri çözümle
        let content: string;
        let screenPosition: { x: number; y: number };

        if (typeof dimensionOrText === 'string') {
            // String overload: show("text", x, y)
            content = `<div style="padding: 4px 8px;">${dimensionOrText}</div>`;
            const xPos = typeof screenPositionOrX === 'number' ? screenPositionOrX : 0;
            const yPos = y ?? 0;
            screenPosition = { x: xPos, y: yPos };
        } else {
            // Dimension overload: show(dimension, {x, y})
            const dimension = dimensionOrText;
            this.currentDimension = dimension;
            content = this.generateContent(dimension);
            screenPosition = (screenPositionOrX as { x: number; y: number }) || { x: 0, y: 0 };
        }

        // Delay ile göster
        this.showTimeout = window.setTimeout(() => {
            this.container.innerHTML = content;

            // Pozisyon hesapla
            const position = this.calculatePosition(screenPosition);
            this.container.style.left = `${position.x}px`;
            this.container.style.top = `${position.y}px`;

            // Göster
            this.container.style.opacity = '1';
            this.container.style.transform = 'translateY(0)';
            this.isVisible = true;
        }, this.config.showDelay || 200);
    }

    /**
     * Tooltip'i gizle
     */
    hide(immediate: boolean = false): void {
        this.clearTimeouts();

        if (immediate) {
            this.hideImmediately();
            return;
        }

        this.hideTimeout = window.setTimeout(() => {
            this.hideImmediately();
        }, this.config.hideDelay);
    }

    /**
     * Hemen gizle
     */
    private hideImmediately(): void {
        this.container.style.opacity = '0';
        this.container.style.transform = 'translateY(5px)';
        this.isVisible = false;
        this.currentDimension = null;
    }

    /**
     * Pozisyon güncelle (mouse hareketi için)
     */
    updatePosition(screenPosition: { x: number; y: number }): void {
        if (!this.isVisible) return;

        const position = this.calculatePosition(screenPosition);
        this.container.style.left = `${position.x}px`;
        this.container.style.top = `${position.y}px`;
    }

    /**
     * Timeout'ları temizle
     */
    private clearTimeouts(): void {
        if (this.hideTimeout !== null) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
        if (this.showTimeout !== null) {
            clearTimeout(this.showTimeout);
            this.showTimeout = null;
        }
    }

    /**
     * Pozisyon hesapla (ekran sınırlarını kontrol et)
     */
    private calculatePosition(
        screenPosition: { x: number; y: number }
    ): { x: number; y: number } {
        const { offset } = this.config;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        // Tooltip boyutlarını al
        const tooltipRect = this.container.getBoundingClientRect();
        const tooltipWidth = tooltipRect.width || 200;
        const tooltipHeight = tooltipRect.height || 80;

        let x = screenPosition.x + offset.x;
        let y = screenPosition.y + offset.y;

        // Sağ sınır kontrolü
        if (x + tooltipWidth > windowWidth - 10) {
            x = screenPosition.x - tooltipWidth - offset.x;
        }

        // Alt sınır kontrolü
        if (y + tooltipHeight > windowHeight - 10) {
            y = screenPosition.y - tooltipHeight - offset.y;
        }

        // Sol sınır kontrolü
        if (x < 10) {
            x = 10;
        }

        // Üst sınır kontrolü
        if (y < 10) {
            y = 10;
        }

        return { x, y };
    }

    /**
     * İçerik oluştur
     */
    private generateContent(dimension: Dimension): string {
        const type = dimension.dimensionType;
        const value = dimension.value;
        const options = dimension.options;

        let html = '';

        // Tip başlığı
        if (this.config.showDimensionType) {
            html += `
                <div style="
                    font-weight: 600;
                    margin-bottom: 4px;
                    color: #4FC3F7;
                    font-size: ${this.config.fontSize + 1}px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                ">
                    <span>${this.getTypeIcon(type)}</span>
                    <span>${this.getTypeLabel(type)}</span>
                </div>
            `;
        }

        // Değer
        html += `
            <div style="
                font-size: ${this.config.fontSize + 2}px;
                margin-bottom: 4px;
                font-weight: 500;
            ">
                ${this.formatValue(value, options)}
            </div>
        `;

        // Tolerans
        if (this.config.showTolerance && options.tolerance) {
            html += `
                <div style="
                    font-size: ${this.config.fontSize - 1}px;
                    color: #90CAF9;
                    margin-bottom: 4px;
                ">
                    Tolerans: ${this.formatTolerance(options.tolerance, options.precision)}
                </div>
            `;
        }

        // ID ve ek bilgiler
        if (this.config.showId) {
            const shortId = dimension.userData.dimensionId?.substring(0, 8) || 'N/A';
            html += `
                <div style="
                    font-size: ${this.config.fontSize - 2}px;
                    color: #888;
                    margin-top: 4px;
                    padding-top: 4px;
                    border-top: 1px solid rgba(255, 255, 255, 0.1);
                ">
                    <span style="opacity: 0.7;">ID:</span> ${shortId}
                    ${options.draggable ? '<span style="margin-left: 8px;">🔄 Sürüklenebilir</span>' : ''}
                </div>
            `;
        }

        return html;
    }

    /**
     * Tip ikonu al
     */
    private getTypeIcon(type: DimensionType): string {
        const icons: Record<DimensionType, string> = {
            [DimensionType.LINEAR]: '📏',
            [DimensionType.HORIZONTAL]: '↔️',
            [DimensionType.VERTICAL]: '↕️',
            [DimensionType.ALIGNED]: '📐',
            [DimensionType.ANGULAR]: '🔄',
            [DimensionType.DIAMETER]: '⌀',
            [DimensionType.RADIUS]: 'R',
            [DimensionType.ARC_LENGTH]: '🌙',
            [DimensionType.ORDINATE]: '📍',
            [DimensionType.CHAIN]: '🔗',
            [DimensionType.BASELINE]: '📊'
        };
        return icons[type] || '📏';
    }

    /**
     * Tip etiketi al
     */
    private getTypeLabel(type: DimensionType): string {
        const labels: Record<DimensionType, string> = {
            [DimensionType.LINEAR]: 'Mesafe',
            [DimensionType.HORIZONTAL]: 'Yatay Mesafe',
            [DimensionType.VERTICAL]: 'Dikey Mesafe',
            [DimensionType.ALIGNED]: 'Hizalı Mesafe',
            [DimensionType.ANGULAR]: 'Açı',
            [DimensionType.DIAMETER]: 'Çap',
            [DimensionType.RADIUS]: 'Yarıçap',
            [DimensionType.ARC_LENGTH]: 'Yay Uzunluğu',
            [DimensionType.ORDINATE]: 'Koordinat',
            [DimensionType.CHAIN]: 'Zincir Ölçü',
            [DimensionType.BASELINE]: 'Baseline Ölçü'
        };
        return labels[type] || type;
    }

    /**
     * Değeri formatla
     */
    private formatValue(value: number, options: any): string {
        let text = value.toFixed(options.precision || 2);

        if (options.prefix) {
            text = options.prefix + text;
        }

        if (options.showUnit && options.unit) {
            text += ' ' + options.unit;
        }

        return `<strong>${text}</strong>`;
    }

    /**
     * Toleransı formatla
     */
    private formatTolerance(tolerance: any, precision: number = 2): string {
        if (tolerance.symmetric) {
            return `±${(tolerance.upper || 0).toFixed(precision)}`;
        }

        let text = '';
        if (tolerance.upper !== undefined) {
            text += `+${tolerance.upper.toFixed(precision)}`;
        }
        if (tolerance.lower !== undefined) {
            text += ` / ${tolerance.lower.toFixed(precision)}`;
        }

        return text || 'Yok';
    }

    /**
     * Resize handler
     */
    private handleResize(): void {
        if (this.isVisible) {
            // Tooltip görünürse yeniden konumlandır
            const rect = this.container.getBoundingClientRect();
            this.updatePosition({ x: rect.left, y: rect.top });
        }
    }

    /**
     * Görünür mü?
     */
    isShowing(): boolean {
        return this.isVisible;
    }

    /**
     * Şu anki dimension
     */
    getCurrentDimension(): Dimension | null {
        return this.currentDimension;
    }

    /**
     * Etkin mi?
     */
    isEnabled(): boolean {
        return this.config.enabled;
    }

    /**
     * Etkinleştir/Devre dışı bırak
     */
    setEnabled(enabled: boolean): void {
        this.config.enabled = enabled;
        if (!enabled) {
            this.hide(true);
        }
    }

    /**
     * Temizle
     */
    dispose(): void {
        this.clearTimeouts();
        this.container.remove();
        window.removeEventListener('resize', this.handleResize.bind(this));
    }
}

// ====================================================================
// EXPORTS
// ====================================================================

export type { TooltipConfig };
